import type { SyntheticEvent } from "react";

/**
 * Rewrite a Supabase Storage public URL to use the on-the-fly image
 * transformation endpoint so we don't ship 3000x3000 source files for
 * 40x40 thumbnails.
 *
 * Converts:
 *   .../storage/v1/object/public/<bucket>/<path>
 * to:
 *   .../storage/v1/render/image/public/<bucket>/<path>?width=...&height=...&resize=cover&quality=...
 *
 * Non-Supabase URLs (and already-transformed URLs) are returned unchanged.
 */
export function transformedImageUrl(
  url: string | null | undefined,
  opts: { width: number; height?: number; quality?: number; resize?: "cover" | "contain" | "fill" } = { width: 96 }
): string | undefined {
  if (!url) return undefined;
  if (!url.includes("/storage/v1/object/public/")) return url;
  const dpr = typeof window !== "undefined" ? Math.min(window.devicePixelRatio || 1, 2) : 2;
  const width = Math.round(opts.width * dpr);
  const height = opts.height ? Math.round(opts.height * dpr) : undefined;
  const quality = opts.quality ?? 70;
  const resize = opts.resize ?? "cover";
  const rendered = url.replace("/storage/v1/object/public/", "/storage/v1/render/image/public/");
  const params = new URLSearchParams();
  params.set("width", String(width));
  if (height) params.set("height", String(height));
  params.set("resize", resize);
  params.set("quality", String(quality));
  return `${rendered}?${params.toString()}`;
}

/**
 * `onError` handler for <img> elements whose `src` came from
 * `transformedImageUrl`. Some WebView / CDN edge cases can fail on the
 * transform endpoint; in that case we retry the untransformed original once
 * and only then fall back to a placeholder.
 */
export function onTransformedImageError(
  originalUrl: string | null | undefined,
  placeholder = "/placeholder.svg",
) {
  return (event: SyntheticEvent<HTMLImageElement>) => {
    const img = event.currentTarget;
    if (originalUrl && img.dataset.imgFallback !== "original" && img.src !== originalUrl) {
      img.dataset.imgFallback = "original";
      img.src = originalUrl;
      return;
    }
    if (img.src.endsWith(placeholder)) return;
    img.dataset.imgFallback = "placeholder";
    img.src = placeholder;
  };
}
